import { useState } from 'react';
import { UserPlus, Loader2, X, CheckCircle2 } from 'lucide-react';
import { executeRecaptcha, RECAPTCHA_ENABLED } from '../utils/recaptcha.js';

/**
 * Модальное окно регистрации (ЧТЗ-AUTH-004, FR-AUTH-17).
 * POST /auth/register {email, password, first_name, last_name, consent_pd, consent_policy}.
 *
 * props:
 *   onRegister(payload) — async, бросает Error с .code при ошибке
 *   onClose()           — закрыть оверлей
 *   onSwitchMode(mode)  — переключить режим ('login')
 */
const ERROR_MESSAGES = {
  EMAIL_ALREADY_EXISTS: 'Пользователь с таким email уже существует.',
  VALIDATION_ERROR: 'Проверьте корректность введённых данных.',
  CONSENT_REQUIRED: 'Необходимо принять оба согласия.',
  SERVICE_UNAVAILABLE: 'Сервис недоступен, попробуйте позже.',
  CAPTCHA_REQUIRED: 'Проверка на робота не пройдена. Обновите страницу и попробуйте снова.',
  CAPTCHA_FAILED: 'Проверка на робота не пройдена. Обновите страницу и попробуйте снова.',
  CAPTCHA_LOW_SCORE: 'Похоже на автоматизированный запрос. Попробуйте ещё раз.',
  REGISTER_FAILED: 'Не удалось зарегистрироваться. Попробуйте позже.',
};

export default function RegisterModal({ onRegister, onClose, onSwitchMode }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [consentPd, setConsentPd] = useState(false);
  const [consentPolicy, setConsentPolicy] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);

  const fieldsFilled =
    email.trim() && password && confirm && firstName.trim() && lastName.trim();
  const canSubmit = fieldsFilled && consentPd && consentPolicy && !busy;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError(null);
    if (password !== confirm) {
      setError('Пароли не совпадают.');
      return;
    }
    if (password.length < 8) {
      setError('Пароль должен быть не короче 8 символов.');
      return;
    }
    setBusy(true);
    try {
      const recaptchaToken = await executeRecaptcha('register');
      await onRegister({
        email,
        password,
        first_name: firstName,
        last_name: lastName,
        consent_pd: consentPd,
        consent_policy: consentPolicy,
        recaptcha_token: recaptchaToken,
      });
      setSuccess(true);
    } catch (err) {
      const code = err?.code || 'REGISTER_FAILED';
      setError(ERROR_MESSAGES[code] || ERROR_MESSAGES.REGISTER_FAILED);
    } finally {
      setBusy(false);
    }
  };

  if (success) {
    return (
      <div className="acc-modal" role="dialog" aria-modal="true" aria-label="Регистрация">
        <div className="acc-modal-head">
          <h2><UserPlus size={16} /> Регистрация</h2>
          <button className="acc-icon-btn" onClick={onClose} aria-label="Закрыть">
            <X size={16} />
          </button>
        </div>
        <div className="acc-body">
          <div className="acc-success" role="status">
            <CheckCircle2 size={16} />
            <span>Регистрация успешна. Проверьте почту для подтверждения.</span>
          </div>
          <button className="acc-btn-primary" type="button" onClick={() => onSwitchMode?.('login')}>
            Перейти ко входу
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="acc-modal" role="dialog" aria-modal="true" aria-label="Регистрация">
      <div className="acc-modal-head">
        <h2><UserPlus size={16} /> Регистрация в CatRUS</h2>
        <button className="acc-icon-btn" onClick={onClose} aria-label="Закрыть">
          <X size={16} />
        </button>
      </div>

      <form className="acc-form" onSubmit={handleSubmit}>
        <div className="acc-field">
          <label htmlFor="reg-email">Email</label>
          <input
            id="reg-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
          />
        </div>

        <div className="acc-field">
          <label htmlFor="reg-first">Имя</label>
          <input
            id="reg-first"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            maxLength={100}
            required
          />
        </div>

        <div className="acc-field">
          <label htmlFor="reg-last">Фамилия</label>
          <input
            id="reg-last"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            maxLength={100}
            required
          />
        </div>

        <div className="acc-field">
          <label htmlFor="reg-password">Пароль</label>
          <input
            id="reg-password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            minLength={8}
            required
          />
          <span className="acc-hint">Минимум 8 символов</span>
        </div>

        <div className="acc-field">
          <label htmlFor="reg-confirm">Подтверждение пароля</label>
          <input
            id="reg-confirm"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="••••••••"
            required
          />
        </div>

        <label className="acc-checkbox">
          <input
            type="checkbox"
            checked={consentPd}
            onChange={(e) => setConsentPd(e.target.checked)}
          />
          <span>
            Согласен на{' '}
            <a href="/consent.html" target="_blank" rel="noopener noreferrer">
              обработку персональных данных
            </a>
          </span>
        </label>

        <label className="acc-checkbox">
          <input
            type="checkbox"
            checked={consentPolicy}
            onChange={(e) => setConsentPolicy(e.target.checked)}
          />
          <span>
            Согласен с{' '}
            <a href="/privacy.html" target="_blank" rel="noopener noreferrer">
              Политикой конфиденциальности
            </a>
          </span>
        </label>

        {error && <div className="acc-error" role="alert">{error}</div>}

        <button className="acc-btn-primary" type="submit" disabled={!canSubmit}>
          {busy ? <Loader2 size={14} className="spin" /> : <UserPlus size={14} />}
          {busy ? 'Регистрация…' : 'Зарегистрироваться'}
        </button>

        <div className="acc-links">
          <button type="button" className="acc-link" onClick={() => onSwitchMode?.('login')}>
            Уже есть аккаунт? Войти
          </button>
        </div>

        {RECAPTCHA_ENABLED && (
          <p className="acc-recaptcha-note">
            Сайт защищён reCAPTCHA; применяются{' '}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Политика конфиденциальности</a>{' и '}
            <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Условия использования</a> Google.
          </p>
        )}
      </form>
    </div>
  );
}
